import { ApplicationState } from 'app/types-store';
import { connect } from 'react-redux';
import { connectedCaseActionCreators } from './actions';
import { ConnectedCasesTileViewProps, ConnectedCasesTileProps } from 'app/area/case/components';
import { tableName } from 'app/area/case/case-data-model';
import { getEntityContext } from 'app/infrastructure/ui/renderer/resolvers';
import { getChildEntitiesFromPath } from 'app/area/case/features/resolvers';
import { deepEquals } from 'app/infrastructure/js';
import { sequence, maxSequenceEntity, minSequenceEntity } from 'app/area/case/features/connected-cases/sequence';
import * as React from 'react';

export const withConnectedCases = (component: any, uiEngineProps: any) => {

  const stateToProps = (appState: ApplicationState): Partial<ConnectedCasesTileViewProps> => {
    const connections = getChildEntitiesFromPath(appState, uiEngineProps, 0, tableName.connectedCases);
    const maxSeq = maxSequenceEntity(connections);

    return {
      maxSequence: sequence(maxSeq),
      stateForCaseIds: appState.caseArea.stateForCaseIds
    };
  };

  const dispatchToProps = (dispatch: any): Partial<ConnectedCasesTileViewProps> => {
    return {
      validateCaseId: (caseId: number) => 
        dispatch(connectedCaseActionCreators.validateCaseId(caseId))
    };
  };

  return connect<any, any, any>(stateToProps, dispatchToProps)(component);
};

export const withConnectionSequenceUpdate = 
  (component: any, uiEngineProps: any) => {
    const stateToProps = (appState: ApplicationState): Partial<ConnectedCasesTileProps> => {
    const connections = getChildEntitiesFromPath(appState, uiEngineProps, 1, tableName.connectedCases);
    const maxSeq = maxSequenceEntity(connections);
    const minSeq = minSequenceEntity(connections);
    const resolveContext = getEntityContext(appState, uiEngineProps);
    const index = connections.findIndex(c => deepEquals(c.id, resolveContext.path));
    const connection = connections.find(c => deepEquals(c.id, resolveContext.path));
    const readMode = uiEngineProps?.hostProps()?.renderMode === 0;
    if(readMode){
      return {
        enabledConnectionOperations: {
          moveUp: index !== 0,
          moveDown: index !== connections.length - 1
        }
      };
    }
    return {
      enabledConnectionOperations: {
        moveUp: sequence(minSeq) !== sequence(connection),
        moveDown: sequence(maxSeq) !== sequence(connection)
      }
    };
  };

  const dispatchToProps = (dispatch: any): Partial<ConnectedCasesTileProps> => {
    return { 
      // TODO: When implementing drag and drop support, it should be possible to calculate the number of steps
      // and pass that in to the action creator.
      moveConnectionUp: (steps: number) => 
        dispatch(connectedCaseActionCreators.changeConnectionSequence(uiEngineProps, steps)),
      moveConnectionDown: (steps: number) => 
        dispatch(connectedCaseActionCreators.changeConnectionSequence(uiEngineProps, -steps)),
      moveConnectionUpPreview: (steps: number, caseId: number, allowWarnings: boolean) => 
        dispatch(connectedCaseActionCreators.changeConnectionSequencePreview(uiEngineProps, steps, caseId, allowWarnings)),
      moveConnectionDownPreview: (steps: number, caseId: number, allowWarnings: boolean) => 
        dispatch(connectedCaseActionCreators.changeConnectionSequencePreview(uiEngineProps, -steps, caseId, allowWarnings))
    };
  };

  return connect<any, any, any>(stateToProps, dispatchToProps)(component);
};

interface ConnectedCasePermissionsProps {
  getPermissions: () => void;
}

export const withConnectedCasePermissions = (Component: any, uiEngineProps: any) => {
  class ConnectedCasePermissions extends React.Component<ConnectedCasePermissionsProps> {
    public componentDidMount() {
      this.props.getPermissions && this.props.getPermissions();
    }

    public componentDidUpdate() {
      this.props.getPermissions && this.props.getPermissions();
    }

    public render() {
      const { getPermissions, ...rest } = this.props;
      return <Component {...rest} />;
    }
  }

  const dispatchToProps = (dispatch: any) => {
    return { 
      getPermissions: () => dispatch(connectedCaseActionCreators.getPermissions(uiEngineProps))
    };
  };

  return connect<any, any, any>(undefined, dispatchToProps)(ConnectedCasePermissions);
};
